import "./ExpenseReportDetails.css";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "../../src/i18n/UseTranslation";
import { useParams } from "react-router-dom";

import { apiCall } from "../api/apiCall";
import { ExpenseReport } from "../models/expense_report";
import { ExpenseReportItem } from "../models/expense_report_item";
import { Attachment } from "../models/attachment";

import Navbar from "../components/Navbar/Navbar";
import TopHeader from "../components/Navbar/TopHeader";
import ExpenseReportDetailsCard from "./ExpenseReportDetailsCard";
import { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth"; 


type ExpenseReportDetailsProps = {
  showNavbar?: boolean;
};

function ExpenseReportDetails({
  showNavbar = true,
}: ExpenseReportDetailsProps) {
  const { t } = useTranslation();

  const translations = t.expenseReportDetails;

  const { expenseReportUuid } = useParams<{
    expenseReportUuid: string;
  }>();

      const [isAuthReady, setIsAuthReady] = useState(false);
  
    useEffect(() => {
            const auth = getAuth(); 
            const unsubscribe = onAuthStateChanged(auth, (user) => {
              setIsAuthReady(true);
            });
            return () => unsubscribe();
          }, []);

  const {
    data: expenseReport,
    isLoading: isLoadingReport,
    isError: isErrorReport,
    error: errorReport,
  } = useQuery<ExpenseReport, Error>({
    queryKey: ["expenseReportDetails", expenseReportUuid],
    enabled: Boolean(expenseReportUuid) && isAuthReady,
    queryFn: () =>
      apiCall<ExpenseReport>(
        `/expense-reports/${expenseReportUuid}`

      ),
  });

  const {
    data: items,
    isLoading: isLoadingItems,
    isError: isErrorItems,
    error: errorItems,
  } = useQuery<ExpenseReportItem[], Error>({
    queryKey: ["expenseReportItems", expenseReportUuid],
    enabled: Boolean(expenseReportUuid) && isAuthReady,
    queryFn: () =>
      apiCall<ExpenseReportItem[]>(
        `/expense-reports/${expenseReportUuid}/items`
      ),
  });

  const {
    data: attachments,
    isLoading: isLoadingAttachments,
    isError: isErrorAttachments,
    error: errorAttachments,
  } = useQuery<Attachment[], Error>({
    queryKey: ["expenseReportAttachments", expenseReportUuid],
    enabled: Boolean(expenseReportUuid) && isAuthReady,
    queryFn: () =>
      apiCall<Attachment[]>(
        `/expense-reports/${expenseReportUuid}/attachments`
      ),
  });

  const formatError = (message: string): string => {
    return translations.errors.generic.replace(
      "{message}",
      message
    );
  };

  if (
    isLoadingReport ||
    isLoadingItems ||
    isLoadingAttachments
  ) {
    return (
      <div className="expense-report-state-page">
        <p className="expense-report-loading">
          {translations.loading}
        </p>
      </div>
    );
  }

  if (isErrorReport) {
    return (
      <div className="expense-report-state-page">
        <p className="expense-report-error">
          {formatError(errorReport.message)}
        </p>
      </div>
    );
  }

  if (isErrorItems) {
    return (
      <div className="expense-report-state-page">
        <p className="expense-report-error">
          {formatError(errorItems.message)}
        </p>
      </div>
    );
  }

  if (isErrorAttachments) {
    return (
      <div className="expense-report-state-page">
        <p className="expense-report-error">
          {formatError(errorAttachments.message)}
        </p>
      </div>
    );
  }

  if (!expenseReport) {
    return (
      <div className="expense-report-state-page">
        <p className="expense-report-error">
          {translations.errors.notFound}
        </p>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper expense-report-page">
      {showNavbar && <Navbar />}

      <TopHeader />

      <main className="expense-report-details-wrapper">
        <ExpenseReportDetailsCard
          expenseReport={expenseReport}
          items={items ?? []}
          attachments={attachments ?? []}
        />
      </main>
    </div>
  );
}

export default ExpenseReportDetails;